import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import {
	GenericTable,
	GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
	SkeletonBlock,
	TableSkeleton,
} from "@ui-components/Skeleton/GenericSkeleton";
import { DataStateView } from "../StateViews";
import { typeColors } from "../../backbone.data";
import type { ConnectorType } from "../../backbone.types";
import {
	buildPredicate,
	type FilterField,
	labelId,
	parseToken,
	type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import {
	makeRoutingApiFields,
	ROUTING_API_TABLE_HEADERS,
	type BbaasRoutingTableDisplayRow,
} from "./routingApiFieldSpec";
import {
	connectedServiceTypeToGroup,
	isVrfFilterUnfiltered,
	matchesConnectorTypeGroupSelection,
	matchesVrfFilterSelection,
} from "../BackboneDetailsData";
import {
	fetchBbaasRoutingTable,
	mapConnectedServiceTypeToConnectorType,
	type BbaasRoutingTableRow,
} from "../../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";

function routingServiceTypeForColors(t: string): ConnectorType {
	return mapConnectedServiceTypeToConnectorType(t);
}

function mapApiRowToDisplay(
	r: BbaasRoutingTableRow,
	idx: number
): BbaasRoutingTableDisplayRow {
	const nullToDash = (v: string | null | undefined) =>
		v != null && String(v).trim() !== "" ? String(v).trim() : "—";
	return {
		rowKey: `${r.connectorId}|${r.prefix}|${r.nextHop}|${idx}`,
		prefix: r.prefix,
		nextHop: r.nextHop,
		sourceName: r.sourceName,
		vrf: r.vrf,
		connectedServiceType: r.connectedServiceType,
		asPath: nullToDash(r.asPath),
		neighborAsn: r.neighborAsn,
		lp: r.lp,
		med: r.med,
		community: nullToDash(r.community),
		origin: nullToDash(r.origin),
		direction: nullToDash(r.direction),
		best: r.best,
	};
}

const ROUTING_MERGE_PAGE_LIMIT = 10_000;

export interface RoutingTabProps {
	vrfFilterSelection: string[];
	/** Applied connector-type group ids (SDWAN | Cloud | Port); empty = no filter. */
	connectorTypeFilterSelection: string[];
	/** Resolved VRF ids from connector list, in filter order; empty when unfiltered or unresolved. */
	routingVrfIds: number[];
	tenantId: number | null;
	bbaasId: number;
	bbaasIdValid: boolean;
	connectorsLoading?: boolean;
}

const RoutingTab: React.FC<RoutingTabProps> = ({
	vrfFilterSelection,
	connectorTypeFilterSelection,
	routingVrfIds,
	tenantId,
	bbaasId,
	bbaasIdValid,
	connectorsLoading = false,
}) => {
	const api = useAxiosAuth0();
	const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
	const [rows, setRows] = useState<BbaasRoutingTableDisplayRow[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [routingLoading, setRoutingLoading] = useState(false);
	const [routingError, setRoutingError] = useState<string | null>(null);
	const [retryToken, setRetryToken] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const isViewAll = isVrfFilterUnfiltered(vrfFilterSelection);
	const isMultiVrf = vrfFilterSelection.length > 1;
	/** Fetch a large window and paginate in the table so connector-type (and search) filters apply to the full set. */
	const routingClientMode =
		isMultiVrf || connectorTypeFilterSelection.length > 0;

	const vrfUnresolved =
		!isViewAll && routingVrfIds.length === 0 && !connectorsLoading;
	const canFetch =
		tenantId != null &&
		bbaasIdValid &&
		(isViewAll || routingVrfIds.length > 0);

	const showLoading =
		routingClientMode || isViewAll
			? routingLoading
			: !vrfUnresolved &&
					((routingVrfIds.length === 0 && connectorsLoading) ||
						(routingVrfIds.length > 0 && routingLoading));

	const currentPageRef = useRef(currentPage);
	const rowsPerPageRef = useRef(rowsPerPage);
	currentPageRef.current = currentPage;
	rowsPerPageRef.current = rowsPerPage;

	const routingVrfIdsKey = routingVrfIds.join(",");
	const vrfSelectionKey = vrfFilterSelection.join("\u0001");

	const routingFetchTrigger = useMemo(
		() =>
			routingClientMode
				? `m:${retryToken}:${routingVrfIdsKey}:${vrfSelectionKey}`
				: `s:${retryToken}:${routingVrfIdsKey}:${vrfSelectionKey}:${currentPage}:${rowsPerPage}`,
		[routingClientMode, retryToken, routingVrfIdsKey, vrfSelectionKey, currentPage, rowsPerPage]
	);

	const connectorTypeKey = connectorTypeFilterSelection.join("\u0001");
	useLayoutEffect(() => {
		setCurrentPage(1);
	}, [routingVrfIdsKey, vrfSelectionKey, connectorTypeKey]);

	const loadRoutingTable = useCallback(async () => {
		if (!canFetch || tenantId == null) {
			setRows([]);
			setTotalCount(0);
			setRoutingLoading(false);
			return;
		}

		if (routingClientMode) {
			if (isMultiVrf) {
				setRoutingLoading(true);
				setRoutingError(null);
				try {
					const results = await Promise.all(
						routingVrfIds.map((vrfId) =>
							fetchBbaasRoutingTable(api, tenantId, bbaasId, {
								vrfId,
								limit: ROUTING_MERGE_PAGE_LIMIT,
								offset: 0,
							})
						)
					);
					const seen = new Set<string>();
					const mergedRaw: BbaasRoutingTableRow[] = [];
					for (const res of results) {
						const body = res.data;
						if (!body?.success || !Array.isArray(body.data)) continue;
						for (const r of body.data) {
							const dedupeKey = `${r.connectorId}|${r.prefix}|${r.nextHop}|${String(r.vrf ?? "")}`;
							if (seen.has(dedupeKey)) continue;
							seen.add(dedupeKey);
							mergedRaw.push(r);
						}
					}
					setRows(mergedRaw.map((r, i) => mapApiRowToDisplay(r, i)));
					setTotalCount(mergedRaw.length);
				} catch (e) {
					setRows([]);
					setTotalCount(0);
					setRoutingError(
						e instanceof Error ? e.message : "Failed to load routing table"
					);
				} finally {
					setRoutingLoading(false);
				}
				return;
			}

			setRoutingLoading(true);
			setRoutingError(null);
			try {
				if (isViewAll) {
					const res = await fetchBbaasRoutingTable(api, tenantId, bbaasId, {
						limit: ROUTING_MERGE_PAGE_LIMIT,
						offset: 0,
					});
					const body = res.data;
					if (!body?.success || !Array.isArray(body.data)) {
						throw new Error(body?.message ?? "Failed to load routing table");
					}
					setRows(body.data.map((r, i) => mapApiRowToDisplay(r, i)));
					setTotalCount(body.data.length);
				} else {
					const id = routingVrfIds[0];
					if (id == null) {
						setRows([]);
						setTotalCount(0);
						setRoutingLoading(false);
						return;
					}
					const res = await fetchBbaasRoutingTable(api, tenantId, bbaasId, {
						vrfId: id,
						limit: ROUTING_MERGE_PAGE_LIMIT,
						offset: 0,
					});
					const body = res.data;
					if (!body?.success || !Array.isArray(body.data)) {
						throw new Error(body?.message ?? "Failed to load routing table");
					}
					setRows(body.data.map((r, i) => mapApiRowToDisplay(r, i)));
					setTotalCount(body.data.length);
				}
			} catch (e) {
				setRows([]);
				setTotalCount(0);
				setRoutingError(
					e instanceof Error ? e.message : "Failed to load routing table"
				);
			} finally {
				setRoutingLoading(false);
			}
			return;
		}

		let vrfIdParam: number | undefined;
		if (!isViewAll) {
			const id = routingVrfIds[0];
			if (id == null) {
				setRows([]);
				setTotalCount(0);
				setRoutingLoading(false);
				return;
			}
			vrfIdParam = id;
		}
		const rpp = rowsPerPageRef.current;
		const page = currentPageRef.current;
		const offset = (page - 1) * rpp;
		setRoutingLoading(true);
		setRoutingError(null);
		try {
			const res = await fetchBbaasRoutingTable(api, tenantId, bbaasId, {
				...(vrfIdParam !== undefined ? { vrfId: vrfIdParam } : {}),
				limit: rpp,
				offset,
			});
			const body = res.data;
			if (!body?.success || !Array.isArray(body.data)) {
				throw new Error(body?.message ?? "Failed to load routing table");
			}
			setRows(body.data.map((r, i) => mapApiRowToDisplay(r, i)));
			setTotalCount(body.pagination?.total ?? body.data.length);
		} catch (e) {
			setRows([]);
			setTotalCount(0);
			setRoutingError(
				e instanceof Error ? e.message : "Failed to load routing table"
			);
		} finally {
			setRoutingLoading(false);
		}
	}, [
		api,
		tenantId,
		bbaasId,
		canFetch,
		isViewAll,
		isMultiVrf,
		routingClientMode,
		routingVrfIds,
		retryToken,
	]);

	useEffect(() => {
		if (!canFetch) {
			setRows([]);
			setTotalCount(0);
			setRoutingLoading(false);
			setRoutingError(null);
			return;
		}
		void loadRoutingTable();
	}, [canFetch, loadRoutingTable, routingFetchTrigger]);

	const routingFields = useMemo(() => makeRoutingApiFields(), []);
	const fieldById = useMemo(() => {
		const m = new Map<string, FilterField<BbaasRoutingTableDisplayRow>>();
		for (const f of routingFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			m.set(id, f);
		}
		return m;
	}, [routingFields]);
	const keyMap = useMemo(() => {
		const m = new Map<string, string>();
		for (const f of routingFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			const label = f.label.toLowerCase();
			m.set(id, id);
			m.set(label, id);
		}
		return m;
	}, [routingFields]);
	const normalizedFilters = useMemo(() => {
		return activeFilters.map((f) => {
			const token = f.op
				? { ...f }
				: parseToken(f.key ? `${f.key}=${f.value}` : f.value);
			if (token.key) {
				const k = token.key.trim().toLowerCase();
				token.key = keyMap.get(k) ?? labelId(token.key);
			}
			const field = token.key ? fieldById.get(token.key) : undefined;
			if (field?.type === "string") {
				const quoted = token.value.match(/^"(.*)"$/);
				if (quoted) {
					token.value = quoted[1];
					token.op = "=";
				} else {
					token.op = ":";
				}
			}
			return token;
		});
	}, [activeFilters, keyMap, fieldById]);
	const predicate = useMemo(
		() =>
			buildPredicate<BbaasRoutingTableDisplayRow>(
				normalizedFilters,
				routingFields
			),
		[normalizedFilters, routingFields]
	);
	const globalFilteredRows = useMemo(
		() =>
			rows.filter((r) => {
				if (!matchesVrfFilterSelection(r.vrf, vrfFilterSelection)) return false;
				return matchesConnectorTypeGroupSelection(
					connectedServiceTypeToGroup(r.connectedServiceType),
					connectorTypeFilterSelection
				);
			}),
		[rows, vrfFilterSelection, connectorTypeFilterSelection]
	);

	const filteredRows = useMemo(
		() => globalFilteredRows.filter(predicate),
		[globalFilteredRows, predicate]
	);

	const columns: GenericTableColumn<BbaasRoutingTableDisplayRow>[] = useMemo(
		() => [
			{
				header: "Prefix",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span
						className={`font-mono text-xs font-medium tabular-nums ${
							r.prefix === "0.0.0.0/0"
								? "text-[var(--color-semantic-text-warning)]"
								: "text-semantic-text-primary"
						}`}
					>
						{r.prefix}
					</span>
				),
				sortAccessor: (r) => r.prefix,
			},
			{
				header: "Neighbor",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span className="font-mono text-xs text-semantic-text-primary tabular-nums">
						{r.nextHop}
					</span>
				),
			},
			{
				header: "Source",
				wrapMode: "truncate",
				renderCell: (r) => (
					<span
						className="block max-w-[160px] truncate text-xs text-semantic-text-primary"
						title={r.sourceName}
					>
						{r.sourceName}
					</span>
				),
				sortAccessor: (r) => r.sourceName,
			},
			{
				header: "VRF",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<GenericTag
						label={r.vrf}
						variant="custom"
						backgroundColor="var(--color-semantic-bg-brand-secondary)"
						textColor="var(--color-semantic-text-brand)"
						size="small"
						rounded="full"
						className="font-medium"
					/>
				),
			},
			{
				header: "Type",
				wrapMode: "nowrap",
				renderCell: (r) => {
					const ct = routingServiceTypeForColors(r.connectedServiceType);
					const tc = typeColors[ct];
					return tc ? (
						<GenericTag
							label={r.connectedServiceType}
							variant="custom"
							backgroundColor={tc.bg}
							textColor={tc.text}
							size="small"
							rounded="full"
						/>
					) : (
						<span className="text-xs">{r.connectedServiceType}</span>
					);
				},
			},
			{
				header: "AS path",
				wrapMode: "truncate",
				renderCell: (r) => (
					<span
						className="block max-w-full truncate font-mono text-xs text-semantic-text-secondary"
						title={r.asPath}
					>
						{r.asPath}
					</span>
				),
			},
			{
				header: "Peer ASN",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span className="text-xs tabular-nums text-semantic-text-primary">
						{r.neighborAsn}
					</span>
				),
			},
			{
				header: "LP",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span
						className={`block text-left text-xs tabular-nums ${
							r.lp !== 100
								? "font-semibold text-semantic-text-brand"
								: "text-semantic-text-primary"
						}`}
					>
						{r.lp}
					</span>
				),
				sortAccessor: (r) => r.lp,
			},
			{
				header: "Med",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span className="text-xs tabular-nums text-semantic-text-primary">
						{r.med}
					</span>
				),
			},
			{
				header: "Comm",
				wrapMode: "truncate",
				renderCell: (r) => (
					<span
						className="block max-w-[120px] truncate font-mono text-[10px] text-semantic-text-secondary"
						title={r.community}
					>
						{r.community}
					</span>
				),
			},
			{
				header: "Origin",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<GenericTag
						label={r.origin}
						variant="default"
						size="small"
						rounded="full"
						className="font-medium"
					/>
				),
			},
			{
				header: "Dir",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span className="text-xs text-semantic-text-secondary">
						{r.direction}
					</span>
				),
			},
			{
				header: "Best",
				wrapMode: "nowrap",
				renderCell: (r) => (
					<span
						className={
							r.best
								? "font-semibold text-semantic-text-brand"
								: "text-semantic-text-secondary"
						}
					>
						{r.best ? "\u2713" : "\u2014"}
					</span>
				),
			},
		],
		[]
	);

	const isEmptyResult =
		vrfUnresolved ||
		(canFetch &&
			!routingLoading &&
			!routingError &&
			filteredRows.length === 0);

	return (
		<DataStateView
			loading={showLoading}
			error={routingError}
			isEmpty={isEmptyResult}
			onRetry={() => setRetryToken((t) => t + 1)}
			loadingContent={
				<GenericCard
					isCollapsible={false}
					className="mt-4"
					headerContent={
						<h2 className="text-md font-bold text-semantic-text-primary">
							Routing table
						</h2>
					}
					bodyContent={
						<div className="flex min-h-0 flex-col gap-4">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<SkeletonBlock h={16} w={72} className="rounded" />
								<SkeletonBlock
									h={32}
									className="min-w-0 max-w-full flex-1 rounded-md lg:max-w-[560px]"
								/>
							</div>
							<TableSkeleton columns={12} rows={8} />
						</div>
					}
				/>
			}
			errorTitle="Failed to load routing table"
			emptyTitle={
				vrfUnresolved ? "VRF not ready" : "No routes in this table"
			}
			emptyDescription={
				vrfUnresolved
					? "We could not resolve the VRF id. Load connectors or pick another VRF."
					: rows.length > 0
						? "No routes matched the backbone filters or search."
						: "No routes matched the current filters or page."
			}
		>
			<GenericCard
				isCollapsible={false}
				className="mt-4"
				headerContent={
					<h2 className="text-md font-bold text-semantic-text-primary">
						Routing table
					</h2>
				}
				bodyContent={
					<div className="flex min-h-0 flex-col">
						<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
							<div className="min-w-0 w-full max-w-full flex-1 sm:max-w-[560px] sm:ml-auto">
								<GenericSearchbar
									placeholder="Find by attribute or value"
									suggestedKeys={ROUTING_API_TABLE_HEADERS}
									onFiltersChange={setActiveFilters}
									inputMaxWidth="100%"
									size="sm"
								/>
							</div>
						</div>

						<div className="min-w-0">
							<GenericTable<BbaasRoutingTableDisplayRow>
								key={routingClientMode ? "routing-client" : "routing-server"}
								columns={columns}
								data={filteredRows}
								initialRowsPerPage={rowsPerPage}
								rowsPerPageOptions={[10, 20, 30]}
								rowKey={(r) => r.rowKey}
								width="100%"
								tableClassName="bg-semantic-bg-primary [&_thead_th]:py-2 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_tbody_td]:py-1.5 [&_tbody_td]:text-xs"
								maxTableBodyHeight="calc(100vh - 28rem)"
								horizontalScroll="always"
								{...(routingClientMode
									? { serverSide: false as const }
									: {
											serverSide: true as const,
											totalCount,
											currentPage,
											rowsPerPage,
											onPageChange: setCurrentPage,
											onRowsPerPageChange: (n: number) => {
												setRowsPerPage(n);
												setCurrentPage(1);
											},
										})}
							/>
						</div>
					</div>
				}
			/>
		</DataStateView>
	);
};

export default RoutingTab;
